import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../components/AuthContext";
import {
  ArrowLeft, LogOut, RefreshCw, Rocket, Loader2, Globe,
  CheckCircle2, XCircle, ExternalLink, Copy, FileText,
  Activity, BarChart3, Tag, Search, TrendingUp, Users, Mail, ShoppingCart,
  Shield, Send,
} from "lucide-react";
import SEO from "../components/SEO";
import { API } from "../apiBase";

export default function AdminSeo() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);
  const [msg, setMsg] = useState("");
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/seo/stats`);
      setStats(data);
    } catch {
      setMsg("Impossible de charger les statistiques.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPushAll = async () => {
    setPinging(true); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/indexnow/notify-sitemap`);
      setMsg(data?.status === "ok"
        ? `✅ ${data.submitted} URLs envoyées à Bing/Yandex/Naver/Seznam`
        : `⚠️ Statut moteur : ${data?.code || data?.error || "inconnu"}`);
      await load();
    } catch {
      setMsg("❌ Échec de la notification IndexNow.");
    } finally {
      setPinging(false);
      setTimeout(() => setMsg(""), 8000);
    }
  };

  const onLogout = () => { logout(); navigate("/admin/login"); };

  const onEmailTest = async () => {
    if (!emailTestTo || !emailTestTo.includes("@")) {
      setEmailTestResult({ ok: false, error: "Adresse email invalide" });
      return;
    }
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/seo/email-test`, { to: emailTestTo });
      setEmailTestResult(data);
      // Refresh stats to update the "last error" indicator
      load();
    } catch (e) {
      setEmailTestResult({ ok: false, error: e?.response?.data?.detail || "Échec inconnu" });
    } finally {
      setEmailTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return true;
      } catch { return false; }
    };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(
          () => { setMsg("📋 Copié"); setTimeout(() => setMsg(""), 2500); },
          () => fallback() && (setMsg("📋 Copié"), setTimeout(() => setMsg(""), 2500))
        );
      } else {
        fallback();
        setMsg("📋 Copié");
        setTimeout(() => setMsg(""), 2500);
      }
    } catch (err) {
      console.warn("[AdminSeo] copy failed:", err);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("fr-FR", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid pt-28 pb-16 px-5 lg:px-8" data-testid="page-admin-seo">
      <SEO title="Admin — Tableau de bord SEO" description="Suivi SEO, GTM, IndexNow et indexation MetaConnect." canonical="/admin/seo" noIndex />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-[12px] text-[#0284C7] hover:text-[#0F2B5B] mb-2">
              <ArrowLeft size={14} /> Retour au dashboard
            </Link>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7]">Référencement & analytics</div>
            <h1 className="font-display text-[#0F2B5B] text-3xl lg:text-4xl mt-1">Tableau de bord SEO</h1>
            <p className="mt-1 text-[13px] text-[var(--mc-muted)]">
              GTM · GA4 · Search Console · Bing · IndexNow · Sitemap
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={load} disabled={loading} data-testid="seo-refresh"
                    className="btn-ghost px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Actualiser
            </button>
            <button onClick={onPushAll} disabled={pinging} data-testid="seo-push-all"
                    className="btn-primary px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait">
              {pinging ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
              {pinging ? "Envoi…" : "Pousser à IndexNow"}
            </button>
            <button onClick={onLogout} className="btn-ghost px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>

        {msg && (
          <div data-testid="seo-feedback"
               className="mc-card px-5 py-3 mb-6 text-[13px] text-[#0F2B5B] border border-[rgba(127,179,255,0.3)]">
            {msg}
          </div>
        )}

        {loading && !stats && <div className="text-[var(--mc-muted)]">Chargement…</div>}

        {stats && (
          <>
            {/* SECTION 1 : Statut technique en temps réel */}
            <SectionTitle icon={<Activity size={16} />} label="Statut technique" caption="Vérifié en direct" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <HealthCard
                label="Sitemap"
                ok={stats.health?.sitemap?.ok}
                status={stats.health?.sitemap?.status}
                detail={stats.sitemap?.url_count ? `${stats.sitemap.url_count} URLs` : null}
                href={stats.sitemap.url}
                testid="health-sitemap"
              />
              <HealthCard
                label="robots.txt"
                ok={stats.health?.robots?.ok}
                status={stats.health?.robots?.status}
                detail={stats.health?.robots?.size ? `${stats.health.robots.size} octets` : null}
                href={stats.sitemap.robots_url}
                testid="health-robots"
              />
              <HealthCard
                label="Clé IndexNow"
                ok={stats.health?.indexnow_key?.ok}
                status={stats.health?.indexnow_key?.status}
                detail={stats.health?.indexnow_key?.size ? `${stats.health.indexnow_key.size} octets` : null}
                href={stats.indexnow.key_url}
                testid="health-indexnow"
              />
              <HealthCard
                label="Consent Mode"
                ok={true}
                status={200}
                detail={stats.tracking?.consent_mode}
                testid="health-consent"
              />
            </div>

            {/* SECTION 2 : Business KPIs */}
            <SectionTitle icon={<TrendingUp size={16} />} label="Conversions" caption="30 derniers jours" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <KPI
                label="Demandes de démo"
                value={stats.business?.demo_requests_30d || 0}
                sub={`${stats.business?.demo_requests_7d || 0} cette semaine · ${stats.business?.demo_requests_total || 0} total`}
                icon={<Mail size={18} />}
                testid="kpi-demos"
              />
              <KPI
                label="Inscriptions Pro"
                value={stats.business?.pro_users_30d || 0}
                sub={`${stats.business?.pro_users_total || 0} comptes au total`}
                icon={<Users size={18} />}
                testid="kpi-pro"
              />
              <KPI
                label="Commandes MSSanté"
                value={stats.business?.mssante_orders_30d || 0}
                sub={`${stats.business?.mssante_orders_total || 0} commandes au total`}
                icon={<ShoppingCart size={18} />}
                testid="kpi-orders"
              />
              <KPI
                label="Articles publiés"
                value={stats.blog?.published || 0}
                sub={`${stats.blog?.drafts || 0} brouillon${(stats.blog?.drafts || 0) > 1 ? "s" : ""}`}
                icon={<FileText size={18} />}
                testid="kpi-blog"
              />
            </div>

            {/* SECTION 3 : IndexNow stats */}
            <SectionTitle icon={<Rocket size={16} />} label="IndexNow" caption="Notifications Bing/Yandex/Naver/Seznam — 30 derniers jours" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              <KPI
                label="Soumissions OK"
                value={stats.indexnow.by_status_30d?.ok?.count || 0}
                icon={<CheckCircle2 size={18} />}
                tone="ok"
                testid="kpi-indexnow-ok"
              />
              <KPI
                label="Soumissions échouées"
                value={stats.indexnow.by_status_30d?.error?.count || 0}
                icon={<XCircle size={18} />}
                tone="err"
                testid="kpi-indexnow-err"
              />
              <KPI
                label="URLs notifiées"
                value={stats.indexnow.total_urls_30d || 0}
                icon={<Rocket size={18} />}
                testid="kpi-indexnow-urls"
              />
            </div>

            {/* SECTION 4 : Configuration tracking */}
            <SectionTitle icon={<Tag size={16} />} label="Configuration tracking" caption="Identifiants et clés actifs" />
            <div className="mc-card p-6 mb-8" data-testid="seo-tracking-config">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ConfigRow
                  label="Google Tag Manager"
                  value={stats.tracking?.gtm_id}
                  mono
                  onCopy={() => copyToClipboard(stats.tracking?.gtm_id)}
                />
                <ConfigRow
                  label="Google Analytics 4"
                  value={stats.tracking?.ga4_id}
                  mono
                  onCopy={() => copyToClipboard(stats.tracking?.ga4_id)}
                />
                <ConfigRow
                  label="Site Verification (Google)"
                  value={stats.tracking?.google_site_verification}
                  mono
                  onCopy={() => copyToClipboard(stats.tracking?.google_site_verification)}
                />
                <ConfigRow
                  label="Clé IndexNow"
                  value={stats.indexnow.key}
                  mono
                  onCopy={() => copyToClipboard(stats.indexnow.key)}
                />
                <ConfigRow
                  label="Hôte canonique"
                  value={stats.indexnow.host}
                />
                <ConfigRow
                  label="Sitemap"
                  value={stats.sitemap.url}
                  link
                />
              </div>
            </div>

            {/* SECTION 5 : Outils externes (raccourcis) */}
            <SectionTitle icon={<ExternalLink size={16} />} label="Outils externes" caption="Accès direct aux dashboards moteurs et analytics" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              <ExternalLinkCard
                title="Google Search Console"
                desc="Indexation Google · 90% trafic FR. Inspection URL, Couverture, Performances."
                url={stats.external_tools?.google_search_console}
                icon={<Search size={18} />}
              />
              <ExternalLinkCard
                title="Google Analytics 4"
                desc="Audience temps réel, sources de trafic, conversions, parcours utilisateurs."
                url={stats.external_tools?.google_analytics}
                icon={<BarChart3 size={18} />}
              />
              <ExternalLinkCard
                title="Google Tag Manager"
                desc="Pilotage des balises (GA4, LinkedIn, Meta Pixel) sans toucher au code."
                url={stats.external_tools?.google_tag_manager}
                icon={<Tag size={18} />}
              />
              <ExternalLinkCard
                title="Bing Webmaster Tools"
                desc="Couvre Bing · Yahoo · DuckDuckGo · Ecosia · Brave."
                url={stats.external_tools?.bing_webmaster}
                icon={<Globe size={18} />}
              />
              <ExternalLinkCard
                title="Yandex Webmaster"
                desc="Indexation pour la Russie et l'Europe de l'Est."
                url={stats.external_tools?.yandex_webmaster}
                icon={<Globe size={18} />}
              />
              <ExternalLinkCard
                title="IndexNow Bing"
                desc="Soumission instantanée de pages. Auto-notifié à chaque publication d'article."
                url={stats.external_tools?.indexnow_bing}
                icon={<Rocket size={18} />}
              />
            </div>

            {/* SECTION 6 : Historique IndexNow */}
            <SectionTitle icon={<FileText size={16} />} label="Historique IndexNow" caption="50 dernières soumissions" />
            <div className="mc-card p-0 overflow-hidden mb-8" data-testid="seo-logs-card">
              {stats.indexnow.logs.length === 0 ? (
                <div className="p-10 text-center text-[var(--mc-muted)] text-[13px]">
                  Aucune soumission pour le moment. Cliquez sur <strong className="text-[#0F2B5B]">« Pousser à IndexNow »</strong> ci-dessus pour démarrer.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="bg-[rgba(127,179,255,0.04)] text-[11px] uppercase tracking-[0.18em] text-[var(--mc-muted)]">
                      <tr>
                        <th className="text-left px-5 py-3 font-medium">Date</th>
                        <th className="text-left px-5 py-3 font-medium">Source</th>
                        <th className="text-left px-5 py-3 font-medium">URLs</th>
                        <th className="text-left px-5 py-3 font-medium">Statut</th>
                        <th className="text-left px-5 py-3 font-medium">Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.indexnow.logs.map((l) => (
                        <tr key={l.id} className="border-t border-[rgba(127,179,255,0.08)] hover:bg-[rgba(127,179,255,0.03)]">
                          <td className="px-5 py-3 text-[#0F2B5B]">{fmtDate(l.timestamp)}</td>
                          <td className="px-5 py-3 text-[var(--mc-muted)]">
                            <code className="text-[11px] text-[#0284C7]">{l.source}</code>
                          </td>
                          <td className="px-5 py-3 text-[#0F2B5B] font-medium">{l.url_count}</td>
                          <td className="px-5 py-3">
                            {l.status === "ok"
                              ? <span className="inline-flex items-center gap-1 text-[var(--mc-mint)]"><CheckCircle2 size={14}/> OK</span>
                              : <span className="inline-flex items-center gap-1 text-[#DC2626]"><XCircle size={14}/> {l.status}</span>}
                          </td>
                          <td className="px-5 py-3 text-[var(--mc-muted)]">{l.code || l.error || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* SECTION : Diagnostic email (Resend) */}
            <SectionTitle icon={<Mail size={16} />} label="Service email" caption="Resend · vérification & diagnostic" />
            <div className="mc-card p-5 mb-8" data-testid="seo-email-diag">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="rounded-xl border border-[rgba(127,179,255,0.15)] bg-[rgba(127,179,255,0.03)] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mc-muted)] mb-1">Statut</div>
                  <div className={`text-[14px] font-medium ${stats.email?.enabled ? "text-[var(--mc-mint)]" : "text-[#DC2626]"}`}>
                    {stats.email?.enabled ? "✓ Configuré" : "✗ Non configuré"}
                  </div>
                </div>
                <div className="rounded-xl border border-[rgba(127,179,255,0.15)] bg-[rgba(127,179,255,0.03)] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mc-muted)] mb-1">Expéditeur</div>
                  <div className="text-[13px] text-[#0F2B5B] font-mono truncate">{stats.email?.sender || "—"}</div>
                </div>
                <div className="rounded-xl border border-[rgba(127,179,255,0.15)] bg-[rgba(127,179,255,0.03)] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mc-muted)] mb-1">Dernière erreur</div>
                  <div className={`text-[12px] ${stats.email?.last_error ? "text-[#DC2626]" : "text-[var(--mc-mint)]"}`}>
                    {stats.email?.last_error || "Aucune"}
                  </div>
                </div>
              </div>

              {stats.email?.last_error && (
                <div className="mb-4 px-4 py-3 rounded-lg border border-[#FFB4B4]/30 bg-[#FFB4B4]/5">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#DC2626] mb-1">⚠️ Diagnostic</div>
                  <div className="text-[12px] text-[#0F2B5B] leading-relaxed">
                    Si le message contient <em>"domain not verified"</em>, allez sur{" "}
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer"
                       className="text-[#0284C7] underline hover:text-[#0F2B5B]">
                      resend.com/domains
                    </a>{" "}
                    pour ajouter les enregistrements DNS (SPF, DKIM, DMARC) de <strong>metaconnect.fr</strong>, puis re-créer une clé API avec accès complet.
                  </div>
                </div>
              )}

              <div className="text-[12px] text-[var(--mc-muted)] mb-2">
                Envoyer un email de test pour valider le service :
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="email"
                  placeholder="votre.email@exemple.fr"
                  value={emailTestTo}
                  onChange={(e) => setEmailTestTo(e.target.value)}
                  data-testid="email-test-input"
                  className="flex-1 min-w-[220px] bg-[rgba(127,179,255,0.05)] border border-[rgba(127,179,255,0.2)] rounded-lg px-3 py-2 text-[13px] text-[#0F2B5B] placeholder:text-[var(--mc-muted)] focus:outline-none focus:border-[var(--mc-blue-300)]"
                />
                <button
                  onClick={onEmailTest}
                  disabled={emailTesting || !emailTestTo}
                  data-testid="email-test-submit"
                  className="btn-primary px-5 py-2 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait">
                  {emailTesting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {emailTesting ? "Envoi…" : "Tester l'envoi"}
                </button>
              </div>
              {emailTestResult && (
                <div
                  data-testid="email-test-result"
                  className={`mt-3 px-4 py-3 rounded-lg text-[12px] ${
                    emailTestResult.ok
                      ? "border border-[var(--mc-mint)]/30 bg-[var(--mc-mint)]/5 text-[#0F2B5B]"
                      : "border border-[#FFB4B4]/30 bg-[#FFB4B4]/5 text-[#0F2B5B]"
                  }`}
                >
                  {emailTestResult.ok ? (
                    <>
                      <strong className="text-[var(--mc-mint)]">✅ Email envoyé</strong> à <code>{emailTestResult.to}</code> · ID: <code className="text-[11px]">{emailTestResult.email_id}</code>
                    </>
                  ) : (
                    <>
                      <strong className="text-[#DC2626]">❌ Échec : </strong>
                      <span className="opacity-90">{emailTestResult.error}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* SECTION 7 : Conformité */}
            <div className="mc-card p-5 flex items-start gap-3" data-testid="seo-rgpd-info">
              <Shield size={18} className="text-[var(--mc-mint)] shrink-0 mt-0.5" />
              <div>
                <div className="text-[13px] text-[#0F2B5B] font-medium mb-1">Conformité RGPD active</div>
                <div className="text-[12px] text-[var(--mc-muted)]">
                  Google Consent Mode v2 configuré : aucun cookie analytics ni publicitaire n'est déposé tant que l'utilisateur n'a pas accepté la bannière. Tous les tags GTM respectent automatiquement ce consentement.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function SectionTitle({ icon, label, caption }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <div className="w-7 h-7 rounded-full bg-[rgba(127,179,255,0.12)] border border-[rgba(127,179,255,0.3)] flex items-center justify-center text-[#0284C7]">
        {icon}
      </div>
      <div>
        <div className="text-[14px] font-medium text-[#0F2B5B]">{label}</div>
        {caption && <div className="text-[11px] text-[var(--mc-muted)]">{caption}</div>}
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon, tone, testid }) {
  let toneClass = "text-[#0F2B5B]";
  if (tone === "ok") toneClass = "text-[var(--mc-mint)]";
  else if (tone === "err") toneClass = "text-[#DC2626]";
  return (
    <div className="mc-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2.5 text-[var(--mc-muted)] text-[11px] uppercase tracking-[0.18em]">
        <span className={toneClass}>{icon}</span>
        {label}
      </div>
      <div className={`font-display text-3xl mt-2 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[12px] text-[var(--mc-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function HealthCard({ label, ok, status, detail, href, testid }) {
  const isOk = ok === true;
  return (
    <div className="mc-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-muted)]">{label}</div>
        {isOk
          ? <CheckCircle2 size={16} className="text-[var(--mc-mint)]" />
          : <XCircle size={16} className="text-[#DC2626]" />}
      </div>
      <div className={`text-[18px] font-display ${isOk ? "text-[#0F2B5B]" : "text-[#DC2626]"}`}>
        {isOk ? "Opérationnel" : `Erreur ${status || ""}`}
      </div>
      {detail && <div className="text-[11px] text-[var(--mc-muted)] mt-1">{detail}</div>}
      {href && (
        <a href={href} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 text-[11px] text-[#0284C7] hover:text-[#0F2B5B] mt-2">
          Voir <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function ConfigRow({ label, value, mono, link, onCopy }) {
  return (
    <div className="rounded-xl border border-[rgba(127,179,255,0.15)] bg-[rgba(127,179,255,0.03)] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mc-muted)] mb-1">{label}</div>
      <div className="flex items-center gap-2">
        {link ? (
          <a href={value} target="_blank" rel="noreferrer"
             className="text-[13px] text-[#0284C7] hover:text-[#0F2B5B] truncate inline-flex items-center gap-1.5 flex-1">
            <span className="truncate">{value}</span>
            <ExternalLink size={12} className="shrink-0" />
          </a>
        ) : (
          <div className={`text-[13px] text-[#0F2B5B] truncate flex-1 ${mono ? "font-mono text-[12px]" : ""}`}>
            {value || "—"}
          </div>
        )}
        {onCopy && value && (
          <button onClick={onCopy} title="Copier" data-testid={`copy-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
                  className="shrink-0 text-[var(--mc-muted)] hover:text-[#0F2B5B] p-1 rounded">
            <Copy size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function ExternalLinkCard({ title, desc, url, icon }) {
  return (
    <a href={url} target="_blank" rel="noreferrer"
       className="mc-card p-5 hover:border-[var(--mc-blue-300)] transition group block">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[rgba(127,179,255,0.12)] border border-[rgba(127,179,255,0.3)] flex items-center justify-center text-[#0284C7] shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[#0F2B5B] text-[15px] group-hover:text-[#0284C7] transition flex items-center gap-1.5">
            {title}
            <ExternalLink size={12} className="text-[var(--mc-muted)] group-hover:text-[#0F2B5B] transition" />
          </div>
          <div className="text-[12px] text-[var(--mc-muted)] mt-1 leading-relaxed">{desc}</div>
        </div>
      </div>
    </a>
  );
}
